/**
 * Pet Bundle Builder v3 — Custom Element for Shopify Dawn
 *
 * v3 changes:
 * - Eye color: color-coded pill chips (not dropdown)
 * - Pet count: simplified 1,2,3,4 circles + "Add a Pet" button
 * - Upload: "Click to Upload" button-style with camera emoji
 * - Error auto-clear: red outlines clear on user interaction
 * - Card header: numbered circle badge + "Pet N"
 * - Warm palette design
 *
 * v4 changes (multi-photo):
 * - Upload accepts up to 4 photos per pet; the FIRST photo is the pet's
 *   main photo (card badge, summary, visible "Pet Photo" cart property).
 *   Extra photos ship as hidden "_Pet Photo 2..4" properties for the
 *   pattern engine, which reads every photo-URL property.
 */

/* Photo cap - shared by the FilePond options and the over-selection trim.
   Mirrors the server's cap (shared/photoLimits.ts DEFAULT_MAX_PET_PHOTOS);
   the server slices to its cap at intake, so these must agree.
   Top-level (outside the custom-element guard, sloppy-mode declarations) so
   tests can execute this file with a stubbed customElements and call them. */
var YL_MAX_PHOTOS_PER_PET = 4;

/* Pure: how many of an attempted selection fit, and what to tell the
   shopper. No browser lets a file picker cap selection count, so the cap is
   applied the moment the picker closes: keep the first photos that fit. */
function ylComputePhotoTrim(currentCount, attemptedCount, max) {
  var room = Math.max(0, max - currentCount);
  var kept = Math.max(0, Math.min(room, attemptedCount));
  var dropped = Math.max(0, attemptedCount - kept);
  var message = null;
  if (dropped > 0) {
    message = kept > 0
      ? 'Up to ' + max + ' photos per pet. We kept the first ' + (kept === 1 ? 'photo' : kept + ' photos') + '.'
      : 'Up to ' + max + ' photos per pet. Remove a photo to add a different one.';
  }
  return { kept: kept, dropped: dropped, message: message };
}

if (!customElements.get('pet-bundle-builder')) {
  class PetBundleBuilder extends HTMLElement {
    constructor() {
      super();
      this._config = null;
      this._petCounter = 1;
      this._currentPetCount = 1;
      this._filePondInstances = new Map();
      this._uploadTimeouts = new Map();
      this._isSubmitting = false;
      this._filepondReady = false;
    }

    /* ------------------------------------------------------------------ */
    /*  Lifecycle                                                          */
    /* ------------------------------------------------------------------ */

    connectedCallback() {
      this.sectionId = this.dataset.sectionId;

      // Cache DOM
      this.cardsContainer = this.querySelector(`#PetCards-${this.sectionId}`);
      this.summaryLines = this.querySelector(`#SummaryLines-${this.sectionId}`);
      this.summaryDiscount = this.querySelector(`#SummaryDiscount-${this.sectionId}`);
      this.totalPrice = this.querySelector(`#TotalPrice-${this.sectionId}`);
      this.listPrice = this.querySelector(`#ListPrice-${this.sectionId}`);
      this.addToCartBtn = this.querySelector(`#AddToCart-${this.sectionId}`);
      this.errorEl = this.querySelector(`#PetError-${this.sectionId}`);
      this.tierBadges = this.querySelector(`#TierBadges-${this.sectionId}`);
      this.template = this.querySelector(`#PetCardTemplate-${this.sectionId}`);
      this.priceDisplay = this.querySelector(`#PriceDisplay-${this.sectionId}`);
      this.countSelector = this.querySelector(`#PetCountSelector-${this.sectionId}`);
      this.addPetBtn = this.querySelector(`#AddPet-${this.sectionId}`);
      this.savingsCallout = this.querySelector(`#SavingsCallout-${this.sectionId}`);
      this.savingsAmount = this.querySelector(`#SavingsAmount-${this.sectionId}`);
      this.savingsCalloutTier = this.querySelector(`#SavingsCalloutTier-${this.sectionId}`);
      this.savingsAmountTier = this.querySelector(`#SavingsAmountTier-${this.sectionId}`);
      this.stickyBar = this.querySelector(`#StickyCta-${this.sectionId}`);
      this.stickyTotal = this.querySelector(`#StickyTotal-${this.sectionId}`);
      this.stickyBtn = this.querySelector(`#StickyBtn-${this.sectionId}`);
      this.addPetLabel = this.querySelector(`#AddPetLabel-${this.sectionId}`);
      this.summaryBox = this.querySelector(`#PetSummary-${this.sectionId}`);
      this.perPetEl = this.querySelector(`#PerPet-${this.sectionId}`);

      // Sticky mobile CTA: appears once the real button scrolls out of view.
      // It proxies the real button so validation/errors stay in one place.
      if (this.stickyBar && this.addToCartBtn && 'IntersectionObserver' in window) {
        this.stickyBtn?.addEventListener('click', () => {
          this.addToCartBtn.click();
          // If validation failed, bring the error (and form) into view.
          setTimeout(() => {
            if (this.errorEl && !this.errorEl.hidden) {
              this.errorEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
          }, 50);
        });
        const io = new IntersectionObserver((entries) => {
          const entry = entries[entries.length - 1];
          // Show only after the shopper has scrolled PAST the button - not
          // while it still lies ahead below the fold on first paint.
          const passed = !entry.isIntersecting && entry.boundingClientRect.top < 0;
          this.stickyBar.hidden = !passed;
          // Reserve the bar's height so it can't cover the last of the page.
          document.body.style.paddingBottom = passed ? `${this.stickyBar.offsetHeight}px` : '';
        }, { rootMargin: '0px 0px -40px 0px' });
        io.observe(this.addToCartBtn);
        this._stickyObserver = io;
      }

      // Reviews link: JSON-template section ids are prefixed at render time,
      // so a static fragment can't match - resolve the widget at click time.
      this.querySelector('a.pet-bundle__reviews')?.addEventListener('click', (e) => {
        const widget = document.querySelector('.jdgm-rev-widg, [id*="17542641549cec9743"]');
        if (widget) {
          e.preventDefault();
          widget.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      });

      // Event delegation
      this.addEventListener('click', this._handleClick.bind(this));
      this.addEventListener('input', this._handleInput.bind(this));
      this.addEventListener('change', this._handleChange.bind(this));

      // Load FilePond programmatically
      this._loadFilePond();

      // Apply default eye color to initial cards based on current species
      this.cardsContainer.querySelectorAll('.pet-card').forEach(card => {
        const activeSpecies = card.querySelector('.pet-card__species-pill--active');
        const species = activeSpecies?.dataset.petType || 'Dog';
        this._applyDefaultEyeColor(card, species);
      });

      // Restore saved form state (if any, < 24h old)
      this._restoreState();

      // Initial summary
      this._updateSummary();
    }

    disconnectedCallback() {
      this._filePondInstances.forEach((pond) => {
        try { pond.destroy(); } catch (e) { /* */ }
      });
      this._filePondInstances.clear();
      if (this._outsideClickHandler) {
        document.removeEventListener('click', this._outsideClickHandler);
        this._outsideClickHandler = null;
      }
    }

    /* ------------------------------------------------------------------ */
    /*  Configuration                                                      */
    /* ------------------------------------------------------------------ */

    get config() {
      if (!this._config) {
        const el = document.getElementById(`PetBundleConfig-${this.sectionId}`);
        if (el) {
          try { this._config = JSON.parse(el.textContent); }
          catch (e) { console.error('PetBundleBuilder: config parse error', e); this._config = {}; }
        }
      }
      return this._config;
    }

    /* ------------------------------------------------------------------ */
    /*  FilePond — Programmatic Loading                                    */
    /* ------------------------------------------------------------------ */

    _loadFilePond() {
      // Self-hosted theme assets first (via the config bridge) - the uploader
      // is checkout-critical and must not depend on a third-party CDN. The
      // unpkg fallback covers section markup that predates the asset list.
      const hosted = Array.isArray(this.config.filepondAssets) && this.config.filepondAssets.length === 4
        ? this.config.filepondAssets
        : null;
      const cdnBase = 'https://unpkg.com';
      const scripts = hosted || [
        `${cdnBase}/filepond@4.31.4/dist/filepond.min.js`,
        `${cdnBase}/filepond-plugin-image-preview@4.6.12/dist/filepond-plugin-image-preview.min.js`,
        `${cdnBase}/filepond-plugin-file-validate-type@1.2.9/dist/filepond-plugin-file-validate-type.min.js`,
        `${cdnBase}/filepond-plugin-file-validate-size@2.2.8/dist/filepond-plugin-file-validate-size.min.js`,
      ];

      this._loadScript(scripts[0]).then(() => {
        return Promise.all(scripts.slice(1).map(src => this._loadScript(src)));
      }).then(() => {
        this._filepondReady = true;
        this._registerFilePondPlugins();
        // Every card that exists by now (restored drafts, early count-pill
        // clicks) gets a live upload zone - not just the first.
        this.cardsContainer.querySelectorAll('.pet-card').forEach((card) => {
          this._initFilePondForCard(card);
        });
      }).catch(err => {
        console.warn('PetBundleBuilder: FilePond load failed', err);
      });
    }

    _loadScript(src) {
      return new Promise((resolve, reject) => {
        if (document.querySelector(`script[src="${src}"]`)) {
          setTimeout(resolve, 50);
          return;
        }
        const s = document.createElement('script');
        s.src = src;
        s.onload = resolve;
        s.onerror = reject;
        document.head.appendChild(s);
      });
    }

    _registerFilePondPlugins() {
      if (!window.FilePond) return;
      const plugins = [];
      if (window.FilePondPluginImagePreview) plugins.push(window.FilePondPluginImagePreview);
      if (window.FilePondPluginFileValidateType) plugins.push(window.FilePondPluginFileValidateType);
      if (window.FilePondPluginFileValidateSize) plugins.push(window.FilePondPluginFileValidateSize);
      if (plugins.length) window.FilePond.registerPlugin(...plugins);
    }

    /**
     * Compress an image file client-side using canvas.
     * Target: max dimension 1600px, JPEG quality 85%.
     * Falls back to original file if compression fails or file is not an image.
     * @param {File} file
     * @returns {Promise<File|Blob>} — compressed blob or original file
     */
    _compressImage(file) {
      return new Promise((resolve) => {
        try {
          if (!file || !file.type || !file.type.startsWith('image/')) {
            return resolve(file);
          }
          // Skip compression for small files (< 500KB) — not worth the overhead
          if (file.size < 500 * 1024) return resolve(file);

          const MAX_DIM = 1600;
          const QUALITY = 0.85;
          const url = URL.createObjectURL(file);
          const img = new Image();

          img.onload = () => {
            try {
              let { width, height } = img;
              const scale = Math.min(1, MAX_DIM / Math.max(width, height));
              width = Math.round(width * scale);
              height = Math.round(height * scale);

              const canvas = document.createElement('canvas');
              canvas.width = width;
              canvas.height = height;
              const ctx = canvas.getContext('2d');
              ctx.drawImage(img, 0, 0, width, height);
              URL.revokeObjectURL(url);

              // Prefer JPEG for compression; fall back to original type for PNGs with transparency needs.
              // For pet photos, JPEG is almost always fine.
              canvas.toBlob(
                (blob) => {
                  if (!blob) return resolve(file);
                  // Only use compressed version if actually smaller
                  if (blob.size >= file.size) return resolve(file);
                  // Wrap in File so FilePond treats it cleanly
                  const compressed = new File([blob], file.name.replace(/\.[^.]+$/, '.jpg'), {
                    type: 'image/jpeg',
                    lastModified: Date.now()
                  });
                  resolve(compressed);
                },
                'image/jpeg',
                QUALITY
              );
            } catch (e) {
              URL.revokeObjectURL(url);
              resolve(file);
            }
          };
          img.onerror = () => {
            URL.revokeObjectURL(url);
            resolve(file);
          };
          img.src = url;
        } catch (e) {
          resolve(file);
        }
      });
    }

    /**
     * Compression starts the moment a file is added (see the addfile warm
     * call) so CPU work overlaps other photos' network time instead of
     * holding an upload slot hostage. Keyed by the File object itself.
     */
    _compressCached(file) {
      if (!this._compressCache) this._compressCache = new WeakMap();
      let p = this._compressCache.get(file);
      if (!p) { p = this._compressImage(file); this._compressCache.set(file, p); }
      return p;
    }

    _getFilePondServerConfig() {
      const config = this.config;
      const compress = (file) => this._compressCached(file);

      const makeProcessor = (url, buildFormData, parseResponse) => {
        return (fieldName, file, metadata, load, error, progress, abort) => {
          let xhr;
          let aborted = false;

          compress(file).then((uploadFile) => {
            if (aborted) return;
            const fd = buildFormData(uploadFile);
            xhr = new XMLHttpRequest();
            xhr.open('POST', url);
            xhr.upload.onprogress = (e) => progress(e.lengthComputable, e.loaded, e.total);
            xhr.onload = function () {
              if (xhr.status >= 200 && xhr.status < 300) {
                try { load(parseResponse(xhr.responseText)); }
                catch (e) { error('Parse error'); }
              } else { error('Upload failed'); }
            };
            xhr.onerror = () => error('Upload failed');
            xhr.send(fd);
          }).catch(() => {
            if (!aborted) error('Upload failed');
          });

          return {
            abort: () => {
              aborted = true;
              if (xhr) xhr.abort();
              abort();
            }
          };
        };
      };

      // Restored-draft photos re-enter as type:'local' files; FilePond
      // fetches them for preview through this passthrough (the upload CDN
      // serves public files with CORS).
      const withLoad = (server) => {
        server.load = (source, load, error, progress, abort) => {
          fetch(String(source))
            .then((r) => { if (!r.ok) throw new Error(String(r.status)); return r.blob(); })
            .then(load)
            .catch(() => error('Could not load photo'));
          return { abort: () => abort() };
        };
        return server;
      };

      if (config.uploadEndpoint) {
        return withLoad({
          process: makeProcessor(
            config.uploadEndpoint,
            (file) => { const fd = new FormData(); fd.append('file', file, file.name); return fd; },
            (text) => {
              try { const r = JSON.parse(text); return r.secure_url || r.url || text; }
              catch (e) { return text; }
            }
          )
        });
      }

      if (config.uploadCloudName && config.uploadPreset) {
        const url = `https://api.cloudinary.com/v1_1/${config.uploadCloudName}/image/upload`;
        return withLoad({
          process: makeProcessor(
            url,
            (file) => {
              const fd = new FormData();
              fd.append('file', file);
              fd.append('upload_preset', config.uploadPreset);
              return fd;
            },
            (text) => {
              const r = JSON.parse(text);
              return r.secure_url || r.url;
            }
          )
        });
      }

      return null;
    }

    _initFilePondForCard(card) {
      if (!card || !this._filepondReady || !window.FilePond) return;

      const fileInput = card.querySelector('.pet-card__file-input');
      if (!fileInput) return;

      const serverConfig = this._getFilePondServerConfig();
      const uploadUrlInput = card.querySelector('.pet-card__upload-url');
      const cardIndex = card.dataset.petIndex;

      this._destroyFilePondForCard(cardIndex);

      const opts = {
        allowMultiple: true,
        maxFiles: YL_MAX_PHOTOS_PER_PET,
        // FilePond's default is 2 parallel uploads - photos 3-4 would queue
        // while the shopper waits. Uploads overlap form-filling instead.
        maxParallelUploads: YL_MAX_PHOTOS_PER_PET,
        // Keep add-order: first added stays first (FilePond default inserts
        // new items at the top, which would silently change the main photo).
        itemInsertLocation: 'after',
        acceptedFileTypes: ['image/jpeg', 'image/png', 'image/webp'],
        maxFileSize: '10MB',
        labelIdle: '\uD83D\uDCF7 <span class="filepond--label-action">' + this._esc(this.config.uploadCtaText || 'Click to Upload') + '</span> \u00B7 up to ' + YL_MAX_PHOTOS_PER_PET + ' photos',
        labelMaxFileSizeExceeded: 'Photo is too large',
        labelMaxFileSize: 'Max size is 10MB',
        imagePreviewHeight: 140,
        stylePanelLayout: 'compact',
        credits: false,
      };

      if (serverConfig) opts.server = serverConfig;

      const pond = window.FilePond.create(fileInput, opts);

      // FilePond rejects an ENTIRE browse/drop batch that would exceed
      // maxFiles and reports it only through this warning event - without a
      // handler, picking 5+ photos uploads nothing and says nothing (the
      // mobile picker makes over-selecting easy). Cap instead: re-add the
      // first photos that fit, tell the shopper what happened. Re-adding
      // never exceeds maxFiles, so this cannot re-fire itself.
      pond.on('warning', (warning, files) => {
        if (!warning || warning.body !== 'Max files') return;
        const attempted = Array.isArray(files) ? files : (files ? [files] : []);
        const trim = ylComputePhotoTrim(pond.getFiles().length, attempted.length, YL_MAX_PHOTOS_PER_PET);
        if (trim.kept > 0) pond.addFiles(attempted.slice(0, trim.kept));
        if (trim.message) {
          const num = this._petNumberFor(card);
          this._showError('Pet ' + num + ': ' + trim.message);
          this._trackUploadIssue('max_photos_trimmed', { pet: num, attempted: attempted.length, kept: trim.kept });
        }
      });

      pond.on('processfile', (error, file) => {
        // Clear upload timeout on success or handled failure
        const timeoutId = this._uploadTimeouts.get(String(cardIndex));
        if (timeoutId) { clearTimeout(timeoutId); this._uploadTimeouts.delete(String(cardIndex)); }

        if (error && file) {
          // Transient mobile blips usually heal on one silent retry. Only a
          // second failure gets loud - and it stays on the thumbnail with
          // FilePond's own tap-to-retry, purchase never blocked.
          if (!this._autoRetried) this._autoRetried = new WeakSet();
          const src = file.file;
          if (src && !this._autoRetried.has(src)) {
            this._autoRetried.add(src);
            setTimeout(() => { try { pond.processFile(file.id).catch(() => {}); } catch (e) { /* item removed */ } }, 1500);
          } else {
            const num = this._petNumberFor(card);
            this._showError(`Pet ${num}: A photo didn't upload. Tap it to retry, or remove it.`);
            this._trackUploadIssue('upload_error', { pet: num, error: String((error && error.body) || error) });
          }
        }

        if (!error && file.serverId) {
          this._syncCardUploads(card, pond);
          const zone = card.querySelector('.pet-card__upload-zone');
          if (zone) zone.classList.remove('pet-card__upload-zone--error');
        }

        // Hide progress indicator on completion
        this._updateUploadProgress(card, null);

        // Persist state now that we have the final URL
        this._saveState();
      });

      // Update custom progress indicator + reset stall timeout on real progress
      pond.on('processfileprogress', (file, progress) => {
        this._updateUploadProgress(card, progress);
        // Reset stall timeout — upload is actively progressing
        this._resetUploadTimeout(cardIndex, card);
      });

      // Local fallback: store data URL
      if (!serverConfig) {
        pond.on('addfile', (error, file) => {
          if (!error && file.file && uploadUrlInput) {
            // Local-only mode keeps just the first photo (dev convenience).
            const files = pond.getFiles();
            if (!files[0] || files[0].id !== file.id) return;
            const reader = new FileReader();
            reader.onload = (e) => {
              uploadUrlInput.value = e.target.result;
              card.dataset.uploadUrls = JSON.stringify([e.target.result]);
              this._updateCardBadgeThumbnails();
              this._updateSummary();
            };
            reader.readAsDataURL(file.file);
            const zone = card.querySelector('.pet-card__upload-zone');
            if (zone) zone.classList.remove('pet-card__upload-zone--error');
          }
        });
      } else {
        // For server upload: also trigger thumbnail update when file is added locally
        pond.on('addfile', (error, file) => {
          if (!error && file.file) {
            this._compressCached(file.file);
            const zone = card.querySelector('.pet-card__upload-zone');
            if (zone) zone.classList.remove('pet-card__upload-zone--error');
            this._refreshCardThumb(card, pond);

            // Start stall-timeout — fires only if no upload progress events
            // arrive within the window (reset by processfileprogress).
            this._resetUploadTimeout(cardIndex, card);
          }
        });
      }

      pond.on('removefile', () => {
        this._syncCardUploads(card, pond);
        this._refreshCardThumb(card, pond);
        // Clear any pending upload timeout
        const pendingTimeout = this._uploadTimeouts.get(String(cardIndex));
        if (pendingTimeout) { clearTimeout(pendingTimeout); this._uploadTimeouts.delete(String(cardIndex)); }
        // Hide progress indicator
        this._updateUploadProgress(card, null);
        this._saveState();
      });

      pond.on('processfilerevert', () => {
        this._syncCardUploads(card, pond);
        this._updateUploadProgress(card, null);
      });
      // Upload-failure messaging lives in the processfile handler (after the
      // quiet retry). Here: recompute completed URLs - never wipe photo 1's
      // value because an optional extra errored - and hide the progress bar.
      pond.on('error', () => {
        this._syncCardUploads(card, pond);
        this._updateUploadProgress(card, null);
      });

      // Hydrate a restored draft. Its URLs were restored into the card
      // dataset, but the pond restarts empty after a reload - the next
      // add/remove would rewrite the dataset from pond files alone and
      // silently clobber the restored photos (and photo 1's hidden value).
      // Re-adding them as local items makes them visible, removable, and
      // countable: COMPLETE_LOAD_ITEM sets serverId to the source URL.
      if (serverConfig) {
        try {
          const restored = JSON.parse(card.dataset.uploadUrls || '[]');
          if (Array.isArray(restored) && restored.length && pond.getFiles().length === 0) {
            const adds = restored.slice(0, YL_MAX_PHOTOS_PER_PET)
              .filter((u) => typeof u === 'string' && u.startsWith('http'))
              .map((u) => pond.addFile(u, { type: 'local' }).catch(() => null));
            Promise.allSettled(adds).then(() => {
              // Reconcile to what actually loaded (a dead URL drops out) and
              // rebuild the badge thumbnail from the first loaded file.
              this._syncCardUploads(card, pond);
              this._refreshCardThumb(card, pond);
            });
          }
        } catch (e) { /* corrupt dataset - draft photos just don't rehydrate */ }
      }

      this._filePondInstances.set(String(cardIndex), pond);
    }

    _destroyFilePondForCard(cardIndex) {
      const key = String(cardIndex);
      const pond = this._filePondInstances.get(key);
      if (pond) {
        try { pond.destroy(); } catch (e) { /* */ }
        this._filePondInstances.delete(key);
      }
    }

    /* ------------------------------------------------------------------ */
    /*  Event Delegation                                                   */
    /* ------------------------------------------------------------------ */

    _handleClick(event) {
      const t = event.target;

      // Species pill
      if (t.closest('.pet-card__species-pill')) {
        this._handleSpeciesToggle(t.closest('.pet-card__species-pill'));
      }
      // Eye color pill
      else if (t.closest('.pet-card__eye-pill')) {
        this._handleEyeColorPill(t.closest('.pet-card__eye-pill'));
      }
      // Quantity buttons
      else if (t.closest('.pet-card__qty-minus')) {
        this._handleQuantityChange(t.closest('.pet-card__qty-minus'), -1);
      }
      else if (t.closest('.pet-card__qty-plus')) {
        this._handleQuantityChange(t.closest('.pet-card__qty-plus'), 1);
      }
      // Remove card
      else if (t.closest('.pet-card__remove')) {
        this._removePetCard(t.closest('.pet-card__remove').dataset.petIndex);
      }
      // Tier badge click (sets pet count)
      else if (t.closest('.pet-bundle__tier-badge')) {
        this._handleTierBadgeClick(t.closest('.pet-bundle__tier-badge'));
      }
      // Pet count pills (1–4)
      else if (t.closest('.pet-bundle__count-pill')) {
        this._handleCountPillClick(t.closest('.pet-bundle__count-pill'));
      }
      // "+" more button — toggle dropdown
      else if (t.closest('.pet-bundle__count-more-btn')) {
        this._toggleMoreDropdown();
      }
      // Dropdown option (5–10)
      else if (t.closest('.pet-bundle__count-more-option')) {
        this._handleMoreOptionClick(t.closest('.pet-bundle__count-more-option'));
      }
      // Header chevron on a complete card: fold it up. Same rule with no
      // card touched - every complete card becomes a row.
      else if (t.closest('.pet-card__collapse')) {
        this._setActiveCard(null);
      }
      // Collapsed card row: tap anywhere (stepper taps matched above) opens
      // this card and rows the rest - the accordion's single rule.
      else if (t.closest('.pet-card__row')) {
        this._setActiveCard(t.closest('.pet-card'));
      }
      // Add a Pet button
      else if (t.closest('.pet-bundle__add-pet')) {
        this._handleAddPet();
      }
      // Add to cart
      else if (t.closest('.pet-bundle__submit')) {
        this._addToCart();
      }
    }

    _handleInput(event) {
      const t = event.target;

      // Auto-clear errors on input
      if (t.closest('.pet-card__name-input')) {
        t.closest('.pet-card__name-input').classList.remove('pet-card__input--error');
        this._updateSummary();
      }
      else if (t.closest('.pet-card__qty-input')) {
        this._updateSummary();
      }
    }

    _handleChange(event) {
      if (event.target.closest('.pet-card__qty-input')) {
        const input = event.target.closest('.pet-card__qty-input');
        let val = parseInt(input.value, 10);
        if (isNaN(val) || val < 1) val = 1;
        if (val > 99) val = 99;
        input.value = val;
        this._updateSummary();
      }
    }

    /* ------------------------------------------------------------------ */
    /*  Eye Color Pill Handler                                             */
    /* ------------------------------------------------------------------ */

    _handleEyeColorPill(pill) {
      const card = pill.closest('.pet-card');
      const pillsContainer = card.querySelector('.pet-card__eye-pills');
      const hiddenInput = card.querySelector('.pet-card__eye-value');

      // Toggle active
      pillsContainer.querySelectorAll('.pet-card__eye-pill').forEach(p => {
        p.classList.remove('pet-card__eye-pill--active');
      });
      pill.classList.add('pet-card__eye-pill--active');
      this._reflectPressed(pillsContainer, '.pet-card__eye-pill', 'pet-card__eye-pill--active');

      // Update hidden input
      if (hiddenInput) hiddenInput.value = pill.dataset.color;

      // Mark that user has explicitly chosen — don't auto-override on species toggle
      card.dataset.eyeUserChose = 'true';

      // Auto-clear error
      pillsContainer.classList.remove('pet-card__eye-pills--error');

      this._updateSummary();
    }

    /**
     * Pre-select the default eye color for a species if the user hasn't
     * explicitly chosen one yet.
     * @param {HTMLElement} card
     * @param {'Dog'|'Cat'} species
     */
    _applyDefaultEyeColor(card, species) {
      // Respect explicit user choice
      if (card.dataset.eyeUserChose === 'true') return;

      const defaultName = species === 'Cat' ? 'green' : 'brown';
      const pillsContainer = card.querySelector('.pet-card__eye-pills');
      const hiddenInput = card.querySelector('.pet-card__eye-value');
      if (!pillsContainer || !hiddenInput) return;

      const pills = pillsContainer.querySelectorAll('.pet-card__eye-pill');
      let targetPill = null;
      pills.forEach(p => {
        if ((p.dataset.color || '').toLowerCase() === defaultName) targetPill = p;
      });
      // Fallback to first pill if exact name not found
      if (!targetPill) targetPill = pills[0];
      if (!targetPill) return;

      pills.forEach(p => p.classList.remove('pet-card__eye-pill--active'));
      targetPill.classList.add('pet-card__eye-pill--active');
      this._reflectPressed(pillsContainer, '.pet-card__eye-pill', 'pet-card__eye-pill--active');
      hiddenInput.value = targetPill.dataset.color || '';
    }

    /* ------------------------------------------------------------------ */
    /*  Pet Count Selector (1, 2, 3, 4)                                    */
    /* ------------------------------------------------------------------ */

    _handleCountPillClick(pill) {
      const count = parseInt(pill.dataset.count, 10);
      if (isNaN(count)) return;

      // Close dropdown if open
      this._closeMoreDropdown();

      this.countSelector.querySelectorAll('.pet-bundle__count-pill').forEach(p => {
        p.classList.remove('pet-bundle__count-pill--active');
      });
      pill.classList.add('pet-bundle__count-pill--active');
      this._reflectPressed(this.countSelector, '.pet-bundle__count-pill', 'pet-bundle__count-pill--active');

      // Deactivate the "+" button
      const moreBtn = this.countSelector.querySelector('.pet-bundle__count-more-btn');
      if (moreBtn) moreBtn.classList.remove('pet-bundle__count-more-btn--active');

      this._setPetCount(count);
    }

    _toggleMoreDropdown() {
      const dropdown = this.countSelector.querySelector('.pet-bundle__count-more-dropdown');
      if (!dropdown) return;
      const isOpen = !dropdown.hidden;
      if (isOpen) {
        this._closeMoreDropdown();
      } else {
        dropdown.hidden = false;
        this.countSelector.querySelector('.pet-bundle__count-more-btn')?.setAttribute('aria-expanded', 'true');
        // Close on outside click or Escape. Persistent (not once:) so a
        // fat-fingered click inside the dropdown's padding doesn't consume
        // the only closer and strand it open.
        setTimeout(() => {
          this._outsideClickHandler = (e) => {
            if (e.type === 'keydown' && e.key !== 'Escape') return;
            if (e.type === 'click' && e.target.closest('.pet-bundle__count-more')) return;
            this._closeMoreDropdown();
          };
          document.addEventListener('click', this._outsideClickHandler);
          document.addEventListener('keydown', this._outsideClickHandler);
        }, 0);
      }
    }

    _closeMoreDropdown() {
      const dropdown = this.countSelector.querySelector('.pet-bundle__count-more-dropdown');
      if (dropdown) dropdown.hidden = true;
      this.countSelector.querySelector('.pet-bundle__count-more-btn')?.setAttribute('aria-expanded', 'false');
      if (this._outsideClickHandler) {
        document.removeEventListener('keydown', this._outsideClickHandler);
        document.removeEventListener('click', this._outsideClickHandler);
        this._outsideClickHandler = null;
      }
    }

    _handleMoreOptionClick(option) {
      const count = parseInt(option.dataset.count, 10);
      if (isNaN(count)) return;

      this._closeMoreDropdown();

      // Deactivate all 1–4 pills
      this.countSelector.querySelectorAll('.pet-bundle__count-pill').forEach(p => {
        p.classList.remove('pet-bundle__count-pill--active');
      });

      // Activate the "+" button and highlight the chosen option
      const moreBtn = this.countSelector.querySelector('.pet-bundle__count-more-btn');
      if (moreBtn) moreBtn.classList.add('pet-bundle__count-more-btn--active');

      // Clear all option highlights, set active one
      this.countSelector.querySelectorAll('.pet-bundle__count-more-option').forEach(o => {
        o.classList.toggle('pet-bundle__count-more-option--active', parseInt(o.dataset.count, 10) === count);
      });

      this._setPetCount(count);
    }

    _handleTierBadgeClick(badge) {
      const tier = parseInt(badge.dataset.tier, 10);
      if (isNaN(tier)) return;

      // Tier 4 means "4+ pets" — set to exactly 4
      const count = tier;
      this._setPetCount(count);
      this._syncCountSelector();
    }

    _handleAddPet() {
      this._addPetCard();
      this._syncCountSelector();
    }

    _setPetCount(count) {
      const currentCards = this.cardsContainer.querySelectorAll('.pet-card');
      const currentCount = currentCards.length;

      if (count === currentCount) return;

      if (count > currentCount) {
        for (let i = currentCount; i < count; i++) {
          this._addPetCard();
        }
      } else {
        const toRemove = Array.from(currentCards).slice(count);
        toRemove.forEach(card => {
          const idx = card.dataset.petIndex;
          this._destroyFilePondForCard(idx);
          card.remove();
        });
        this._reindexCards();
        this._updateSummary();
      }

      this._currentPetCount = count;
    }

    /* ------------------------------------------------------------------ */
    /*  Species Toggle (compact pills)                                     */
    /* ------------------------------------------------------------------ */

    _handleSpeciesToggle(pill) {
      const card = pill.closest('.pet-card');
      const pills = card.querySelector('.pet-card__species-pills');
      const hiddenInput = card.querySelector('.pet-card__variant-input');

      pills.querySelectorAll('.pet-card__species-pill').forEach(p => {
        p.classList.remove('pet-card__species-pill--active');
      });
      pill.classList.add('pet-card__species-pill--active');
      this._reflectPressed(pills, '.pet-card__species-pill', 'pet-card__species-pill--active');

      if (hiddenInput) hiddenInput.value = pill.dataset.variantId;

      // Update eye color default if user hasn't explicitly chosen one
      this._applyDefaultEyeColor(card, pill.dataset.petType);

      this._swapGalleryMedia(pill.dataset.variantId);
      this._updateSummary();
    }

    _swapGalleryMedia(variantId) {
      const mediaMap = this.config.variantMediaMap;
      if (!mediaMap) return;

      const mediaId = mediaMap[variantId];
      if (!mediaId) return;

      const gallery = this.querySelector('media-gallery');
      if (gallery && typeof gallery.setActiveMedia === 'function') {
        // Dawn's setActiveMedia calls window.scrollTo to scroll the gallery
        // into view. Temporarily block all scrollTo/scroll calls so the user
        // stays where they are in the form.
        const origScrollTo = window.scrollTo;
        const origScroll = window.scroll;
        window.scrollTo = () => {};
        window.scroll = () => {};
        gallery.setActiveMedia(mediaId, false);
        // Restore after Dawn's sync + async scroll attempts complete.
        setTimeout(() => {
          window.scrollTo = origScrollTo;
          window.scroll = origScroll;
        }, 100);
      }
    }

    /* ------------------------------------------------------------------ */
    /*  Quantity                                                            */
    /* ------------------------------------------------------------------ */

    _handleQuantityChange(btn, delta) {
      const card = btn.closest('.pet-card');
      const input = card.querySelector('.pet-card__qty-input');
      let val = parseInt(input.value, 10) || 1;
      val = Math.max(1, Math.min(99, val + delta));
      input.value = val;
      this._updateSummary();
    }

    /* ------------------------------------------------------------------ */
    /*  Card Management                                                    */
    /* ------------------------------------------------------------------ */

    _addPetCard() {
      if (!this.template) return;

      this._petCounter++;
      const clone = this.template.content.cloneNode(true);
      const card = clone.querySelector('.pet-card');

      card.dataset.petIndex = this._petCounter;

      // Update badge and heading
      const badge = card.querySelector('.pet-card__badge');
      if (badge) badge.textContent = this._petCounter;

      const heading = card.querySelector('.pet-card__number');
      if (heading) heading.textContent = `Pet ${this._petCounter}`;

      const removeBtn = card.querySelector('.pet-card__remove');
      if (removeBtn) removeBtn.dataset.petIndex = this._petCounter;

      // Update IDs
      card.querySelectorAll('[id]').forEach(el => {
        el.id = el.id.replace(/-0$/, `-${this._petCounter}`);
      });
      card.querySelectorAll('[for]').forEach(el => {
        el.setAttribute('for', el.getAttribute('for').replace(/-0$/, `-${this._petCounter}`));
      });

      // Reset values
      const nameInput = card.querySelector('.pet-card__name-input');
      if (nameInput) nameInput.value = '';

      const eyeValue = card.querySelector('.pet-card__eye-value');
      if (eyeValue) eyeValue.value = '';

      // Reset eye color pills
      card.querySelectorAll('.pet-card__eye-pill').forEach(p => {
        p.classList.remove('pet-card__eye-pill--active');
      });

      // Clear user-chose flag so default applies
      card.dataset.eyeUserChose = 'false';

      const qtyInput = card.querySelector('.pet-card__qty-input');
      if (qtyInput) qtyInput.value = '1';

      const uploadUrl = card.querySelector('.pet-card__upload-url');
      if (uploadUrl) uploadUrl.value = '';
      delete card.dataset.uploadUrls;
      delete card.dataset.thumbUrl;

      // Reset species to Dog
      card.querySelectorAll('.pet-card__species-pill').forEach(p => {
        p.classList.remove('pet-card__species-pill--active');
        if (p.dataset.petType === 'Dog') p.classList.add('pet-card__species-pill--active');
        p.setAttribute('aria-pressed', p.dataset.petType === 'Dog' ? 'true' : 'false');
      });
      const variantInput = card.querySelector('.pet-card__variant-input');
      if (variantInput) variantInput.value = this.config.dogVariantId;

      card.classList.add('pet-card--entering');

      this.cardsContainer.appendChild(clone);

      // Init FilePond on new card
      const addedCard = this.cardsContainer.querySelector(`.pet-card[data-pet-index="${this._petCounter}"]`);
      if (addedCard && this._filepondReady) {
        this._initFilePondForCard(addedCard);
      }

      // Apply default eye color based on species (Dog=Brown default)
      if (addedCard) this._applyDefaultEyeColor(addedCard, 'Dog');

      // The new card is where the user works now - everything else rows up.
      if (addedCard) this._setActiveCard(addedCard);

      this._reindexCards();
      this._updateSummary();
    }

    _removePetCard(index) {
      const card = this.cardsContainer.querySelector(`.pet-card[data-pet-index="${index}"]`);
      if (!card) return;

      this._destroyFilePondForCard(index);

      // Removal must never depend on the exit animation actually running -
      // reduced-motion and animation-stripping extensions disable it, and a
      // card left in the DOM keeps counting toward the price and the cart.
      // animationend wins normally; the timeout guarantees it regardless.
      let removed = false;
      const finish = () => {
        if (removed) return;
        removed = true;
        card.remove();
        this._reindexCards();
        this._updateSummary();
        this._syncCountSelector();
      };
      card.classList.add('pet-card--removing');
      card.addEventListener('animationend', finish, { once: true });
      setTimeout(finish, 300);
    }

    /** Complete = every required field valid: uploaded photo, name, eye. */
    _isCardComplete(card) {
      const d = this._getCardData(card);
      return !!(d.petName && d.eyeColor && d.uploadUrl && d.uploadUrl.startsWith('http'));
    }

    /**
     * The accordion rule, one qualifier: the card you touch is open, every
     * other COMPLETE card is a row. Incomplete cards never collapse - unseen
     * unfinished work is how submit-time surprises happen, and collapsing
     * doubles as the completion reward. Pass null to row up all complete
     * cards. Collapsing is a class flip - FilePond instances and inputs stay
     * alive in the hidden body.
     */
    _setActiveCard(activeCard) {
      this.cardsContainer.querySelectorAll('.pet-card').forEach((card) => {
        const collapsed = card !== activeCard && this._isCardComplete(card);
        card.classList.toggle('pet-card--collapsed', collapsed);
        const btn = card.querySelector('.pet-card__row-open');
        if (btn) btn.setAttribute('aria-expanded', String(!collapsed));
      });
      if (activeCard) {
        activeCard.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
      this._updateSummary();
    }

    _reindexCards() {
      const cards = this.cardsContainer.querySelectorAll('.pet-card');
      cards.forEach((card, i) => {
        const badge = card.querySelector('.pet-card__badge');
        if (badge) badge.textContent = i + 1;

        const heading = card.querySelector('.pet-card__number');
        if (heading) heading.textContent = `Pet ${i + 1}`;

        const removeBtn = card.querySelector('.pet-card__remove');
        if (removeBtn) removeBtn.setAttribute('aria-label', `Remove pet ${i + 1}`);
      });

      // Ensure first card is not removable
      if (cards.length > 0) {
        const firstRemoveBtn = cards[0].querySelector('.pet-card__remove');
        if (firstRemoveBtn) firstRemoveBtn.style.display = cards.length > 1 ? '' : 'none';
      }
    }

    _syncCountSelector() {
      const count = this.cardsContainer.querySelectorAll('.pet-card').length;
      this._currentPetCount = count;

      // Highlight 1–4 pills
      const pills = this.countSelector.querySelectorAll('.pet-bundle__count-pill');
      pills.forEach(p => {
        const c = parseInt(p.dataset.count, 10);
        p.classList.toggle('pet-bundle__count-pill--active', c === count);
      });

      this._reflectPressed(this.countSelector, '.pet-bundle__count-pill', 'pet-bundle__count-pill--active');

      // Handle 5+ via the "+" button
      const moreBtn = this.countSelector.querySelector('.pet-bundle__count-more-btn');
      if (count > 4) {
        if (moreBtn) moreBtn.classList.add('pet-bundle__count-more-btn--active');
        this.countSelector.querySelectorAll('.pet-bundle__count-more-option').forEach(o => {
          o.classList.toggle('pet-bundle__count-more-option--active', parseInt(o.dataset.count, 10) === count);
        });
      } else {
        if (moreBtn) moreBtn.classList.remove('pet-bundle__count-more-btn--active');
        this.countSelector.querySelectorAll('.pet-bundle__count-more-option').forEach(o => {
          o.classList.remove('pet-bundle__count-more-option--active');
        });
      }

      this._updateCardBadgeThumbnails();
    }

    _updateCardBadgeThumbnails() {
      const cards = this.cardsContainer.querySelectorAll('.pet-card');

      cards.forEach((card, i) => {
        const badge = card.querySelector('.pet-card__badge');
        if (!badge) return;

        const uploadUrl = card.querySelector('.pet-card__upload-url')?.value || '';
        const thumbUrl = card.dataset.thumbUrl || '';
        const src = thumbUrl || (uploadUrl && uploadUrl.startsWith('data:') ? uploadUrl : '');

        if (src) {
          badge.classList.add('pet-card__badge--has-thumb');
          if (!badge.querySelector('img')) {
            badge.innerHTML = `<img src="${src}" alt="Pet ${i + 1}">`;
          } else {
            badge.querySelector('img').src = src;
          }
        } else {
          badge.classList.remove('pet-card__badge--has-thumb');
          badge.textContent = String(i + 1);
        }
      });
    }

    /* ------------------------------------------------------------------ */
    /*  Multi-photo helpers (v4)                                           */
    /* ------------------------------------------------------------------ */

    /**
     * Rebuild the card's ordered upload-URL list from FilePond's file list.
     * The FIRST photo is the pet's main photo: it drives the card badge,
     * order summary, and the visible "Pet Photo" cart property; the rest
     * ride along as hidden "_Pet Photo N" properties for the pattern engine.
     */
    _syncCardUploads(card, pond) {
      const urls = pond.getFiles()
        .map(f => (typeof f.serverId === 'string' ? f.serverId : ''))
        .filter(u => u.startsWith('http'));
      card.dataset.uploadUrls = JSON.stringify(urls);
      const uploadUrlInput = card.querySelector('.pet-card__upload-url');
      if (uploadUrlInput) uploadUrlInput.value = urls[0] || '';
    }

    /** Card badge/summary thumbnail always mirrors the FIRST photo. */
    _refreshCardThumb(card, pond) {
      const first = pond.getFiles()[0];
      if (!first || !first.file) {
        delete card.dataset.thumbUrl;
        this._updateCardBadgeThumbnails();
        this._updateSummary();
        return;
      }
      const reader = new FileReader();
      reader.onload = (e) => {
        card.dataset.thumbUrl = e.target.result;
        this._updateCardBadgeThumbnails();
        this._updateSummary();
      };
      reader.readAsDataURL(first.file);
    }

    /* ------------------------------------------------------------------ */
    /*  Data Extraction                                                    */
    /* ------------------------------------------------------------------ */

    _getCardData(card) {
      const activePill = card.querySelector('.pet-card__species-pill--active');
      const uploadUrl = card.querySelector('.pet-card__upload-url')?.value || '';
      let uploadUrls = [];
      try { uploadUrls = JSON.parse(card.dataset.uploadUrls || '[]'); } catch (e) { uploadUrls = []; }
      if (!Array.isArray(uploadUrls)) uploadUrls = [];
      if (!uploadUrls.length && uploadUrl) uploadUrls = [uploadUrl];
      const thumbUrl = card.dataset.thumbUrl || '';
      return {
        variantId: card.querySelector('.pet-card__variant-input')?.value || this.config.dogVariantId,
        petType: activePill?.dataset.petType || 'Dog',
        petName: card.querySelector('.pet-card__name-input')?.value.trim() || '',
        eyeColor: card.querySelector('.pet-card__eye-value')?.value || '',
        uploadUrl: uploadUrls[0] || uploadUrl,
        uploadUrls: uploadUrls,
        thumbUrl: thumbUrl || (uploadUrl && uploadUrl.startsWith('data:') ? uploadUrl : ''),
        quantity: Math.max(1, parseInt(card.querySelector('.pet-card__qty-input')?.value, 10) || 1),
        index: card.dataset.petIndex,
      };
    }

    _getAllCardData() {
      return Array.from(this.cardsContainer.querySelectorAll('.pet-card')).map(c => this._getCardData(c));
    }

    _getTotalPetCount() {
      return this._getAllCardData().reduce((sum, p) => sum + p.quantity, 0);
    }

    /* ------------------------------------------------------------------ */
    /*  Discount Calculation                                               */
    /* ------------------------------------------------------------------ */

    _getDiscountAmountCents(totalPets) {
      const t = this.config.discountTiers;
      const perPet = this.config.discountPerPetAbove5 || 1100; // $11/pet default
      if (totalPets > 5) return (t['5'] || 0) + ((totalPets - 5) * perPet);
      if (totalPets === 5) return t['5'] || 0;
      if (totalPets === 4) return t['4'] || 0;
      if (totalPets === 3) return t['3'] || 0;
      if (totalPets === 2) return t['2'] || 0;
      return 0;
    }

    _getDiscountedTotalCents(totalPets) {
      return (this.config.basePrice * totalPets) - this._getDiscountAmountCents(totalPets);
    }

    /* ------------------------------------------------------------------ */
    /*  Money Formatting                                                   */
    /* ------------------------------------------------------------------ */

    _formatMoney(cents) {
      const amount = (cents / 100).toFixed(2);
      const noDecimals = Math.round(cents / 100).toString();
      const withComma = amount.replace('.', ',');
      let fmt = this.config.moneyFormat || '${{amount}}';
      return fmt
        .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, withComma)
        .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, noDecimals.replace(/\B(?=(\d{3})+(?!\d))/g, ','))
        .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, noDecimals)
        .replace(/\{\{\s*amount\s*\}\}/g, amount);
    }

    /* ------------------------------------------------------------------ */
    /*  Summary & Tier Updates                                             */
    /* ------------------------------------------------------------------ */

    _updateSummary() {
      const petData = this._getAllCardData();
      const totalPets = petData.reduce((sum, p) => sum + p.quantity, 0);
      const discountAmount = this._getDiscountAmountCents(totalPets);
      const bundleTotal = this._getDiscountedTotalCents(totalPets);
      const basePrice = this.config.basePrice;

      this._updateTierBadges(totalPets);
      this._updateSavingsCallout(totalPets);
      this._updateCardProgress();
      this._updateCardBadgeThumbnails();

      // Price display
      if (this.priceDisplay) {
        if (discountAmount > 0) {
          const perUnit = Math.round(bundleTotal / totalPets);
          this.priceDisplay.innerHTML = `${this._formatMoney(perUnit)} each <span class="pet-bundle__price-compare">${this._formatMoney(basePrice)}</span>`;
        } else {
          this.priceDisplay.textContent = this._formatMoney(basePrice);
        }
      }

      // Summary lines (with optional photo thumbnails)
      if (this.summaryLines) {
        this.summaryLines.innerHTML = petData.map((pet, i) => {
          const name = pet.petName || `Pet ${i + 1}`;
          const lineTotal = basePrice * pet.quantity;
          const thumbHtml = pet.thumbUrl
            ? `<img class="pet-bundle__summary-thumb" src="${pet.thumbUrl}" alt="${this._esc(name)}">`
            : '';
          return `<div class="pet-bundle__summary-line">
            <span class="pet-bundle__summary-line-name">
              ${thumbHtml}
              ${this._esc(name)} (${pet.petType})
              ${pet.quantity > 1 ? `<span class="pet-bundle__summary-line-qty">&times;${pet.quantity}</span>` : ''}
            </span>
            <span>${this._formatMoney(lineTotal)}</span>
          </div>`;
        }).join('');
      }

      // Discount line
      if (this.summaryDiscount) {
        if (discountAmount > 0) {
          this.summaryDiscount.hidden = false;
          const el = this.summaryDiscount.querySelector('.pet-bundle__summary-discount-amount');
          if (el) el.textContent = `-${this._formatMoney(discountAmount)}`;
        } else {
          this.summaryDiscount.hidden = true;
        }
      }

      // Total (with list price strikethrough when discounted)
      if (this.totalPrice) {
        this.totalPrice.textContent = this._formatMoney(bundleTotal);
      }
      if (this.listPrice) {
        if (discountAmount > 0) {
          this.listPrice.textContent = this._formatMoney(basePrice * totalPets);
          this.listPrice.hidden = false;
        } else {
          this.listPrice.hidden = true;
        }
      }

      if (this.stickyTotal) {
        this.stickyTotal.textContent = this._formatMoney(bundleTotal);
      }
      // Single-pet orders (68% of buyers) get a one-line total instead of
      // the three-row summary ceremony.
      if (this.summaryBox) {
        this.summaryBox.classList.toggle('pet-bundle__summary--compact', totalPets === 1);
      }
      // Per-pet framing is the strongest version of the bundle story:
      // "2 pets: $49 each" beats "$10 off". Total savings ride along so the
      // shopper never has to do the math themselves.
      if (this.perPetEl) {
        if (totalPets > 1) {
          const savedText = discountAmount > 0
            ? ` · you save ${this._formatMoney(discountAmount).replace(/\.00$/, '')}`
            : '';
          this.perPetEl.textContent = `${totalPets} kits: ${this._formatMoney(Math.round(bundleTotal / totalPets))} each${savedText}`;
          this.perPetEl.hidden = false;
        } else {
          this.perPetEl.hidden = true;
        }
      }
      this._updateCtaText();
      this._updateAddPetLabel();

      // Auto-save form state to localStorage (fire-and-forget)
      this._saveState();
    }

    /** CTA copy personalizes once Pet 1 has a name: "Make Momo's Kit". */
    _updateCtaText() {
      const pets = this._getAllCardData();
      const total = pets.reduce((s, p) => s + p.quantity, 0);
      const firstName = (pets[0]?.petName || '').trim();
      let label = 'Add to Cart';
      if (total > 1) {
        label = `Add ${total} Kits to Cart`;
      } else if (firstName) {
        const safe = firstName.length > 14 ? `${firstName.slice(0, 14)}…` : firstName;
        label = `Make ${safe}'s Kit`;
      }
      this.querySelectorAll('.pet-bundle__submit-text').forEach((el) => {
        el.textContent = label;
      });
    }

    /** "+ Add another pet" always shows what the NEXT pet saves. */
    _updateAddPetLabel() {
      if (!this.addPetLabel) return;
      const n = Math.max(1, this._getTotalPetCount());
      const nextSavings =
        (this._getDiscountAmountCents(n + 1) - this._getDiscountAmountCents(n)) / 100;
      this.addPetLabel.textContent =
        nextSavings > 0
          ? `+ Add another pet \u00b7 save $${nextSavings}`
          : '+ Add another pet';
    }

    /**
     * Keeps the collapsed row and the completion footer truthful. Pure render
     * state - no triggers: complete cards show meta + stepper + footer,
     * incomplete ones show progress in the row.
     */
    _updateCardCollapsedUI(card, data, filled, total) {
      const complete = filled === total;
      const name = data.petName || `Pet ${this._petNumberFor(card)}`;

      const rowName = card.querySelector('.pet-card__row-name');
      if (rowName) rowName.textContent = name;
      const openBtn = card.querySelector('.pet-card__row-open');
      if (openBtn) openBtn.setAttribute('aria-label', `Edit ${name}`);

      const meta = card.querySelector('.pet-card__row-meta');
      if (meta) {
        meta.textContent = complete
          ? `${data.petType} · ${data.eyeColor} eyes`
          : `${filled}/${total} complete · tap to finish`;
        meta.classList.toggle('pet-card__row-meta--incomplete', !complete);
      }

      const thumb = card.querySelector('.pet-card__row-thumb');
      if (thumb) {
        const src = data.thumbUrl || '';
        if (src) {
          if (!thumb.querySelector('img')) thumb.innerHTML = `<img src="${src}" alt="">`;
          else thumb.querySelector('img').src = src;
        } else {
          thumb.textContent = '🐾';
        }
      }

      const collapseBtn = card.querySelector('.pet-card__collapse');
      if (collapseBtn) {
        collapseBtn.hidden = !complete;
        collapseBtn.setAttribute('aria-label', `Done with ${name}`);
      }

      const rowQty = card.querySelector('.pet-card__quantity--row');
      if (rowQty) rowQty.hidden = !complete;
      const qtyDisplay = card.querySelector('.pet-card__qty-display');
      if (qtyDisplay) qtyDisplay.textContent = String(data.quantity);

      const footer = card.querySelector('.pet-card__footer');
      if (footer) {
        footer.hidden = !complete;
        const title = footer.querySelector('.pet-card__footer-title');
        const sub = footer.querySelector('.pet-card__footer-sub');
        if (title && sub) {
          if (data.quantity === 1) {
            title.textContent = `Add a 2nd ${name} kit`;
            sub.textContent = 'One to keep, one to gift.';
          } else {
            const words = ['zero', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine'];
            const gifts = data.quantity - 1;
            title.textContent = `${data.quantity} ${name} kits`;
            sub.textContent = `One to keep, ${words[gifts] || gifts} to gift.`;
          }
        }
      }
    }

    _updateSavingsCallout(totalPets) {
      const discountAmount = this._getDiscountAmountCents(totalPets);
      const show = discountAmount > 0;
      const threshold = this.config.freeShippingThresholdCents || 0;
      const shipsFree = threshold > 0 && this._getDiscountedTotalCents(totalPets) >= threshold;
      const savingsText = show
        ? this._formatMoney(discountAmount) + (shipsFree ? ' + Free Shipping' : '')
        : null;

      [this.savingsCallout, this.savingsCalloutTier].forEach(el => {
        if (!el) return;
        el.classList.toggle('pet-bundle__savings-callout--visible', show);
      });
      [this.savingsAmount, this.savingsAmountTier].forEach(el => {
        if (!el || !savingsText) return;
        el.textContent = savingsText;
      });
    }

    _updateCardProgress() {
      const cards = this.cardsContainer.querySelectorAll('.pet-card');
      cards.forEach(card => {
        const progressFill = card.querySelector('.pet-card__progress-fill');
        const progressText = card.querySelector('.pet-card__progress-text');
        if (!progressFill || !progressText) return;

        const data = this._getCardData(card);
        const pondInstance = this._filePondInstances.get(String(data.index));

        let filled = 0;
        const total = 3; // name, eye color, photo
        if (data.petName) filled++;
        if (data.eyeColor) filled++;
        const hasFile = data.uploadUrl || (pondInstance && pondInstance.getFiles().length > 0);
        if (hasFile) filled++;

        this._updateCardCollapsedUI(card, data, filled, total);

        const pct = Math.round((filled / total) * 100);
        progressFill.style.setProperty('width', pct + '%', 'important');
        progressFill.dataset.filled = String(filled);

        if (filled === total) {
          progressFill.classList.add('pet-card__progress-fill--complete');
          progressText.classList.add('pet-card__progress-text--complete');
          progressText.textContent = '✓ Complete';
        } else {
          progressFill.classList.remove('pet-card__progress-fill--complete');
          progressText.classList.remove('pet-card__progress-text--complete');
          progressText.textContent = `${filled}/${total} complete`;
        }
      });
    }

    _updateTierBadges(totalPets) {
      if (!this.tierBadges) return;
      this.tierBadges.querySelectorAll('.pet-bundle__tier-badge').forEach(badge => {
        const tier = parseInt(badge.dataset.tier, 10);
        const active = (tier === 5 && totalPets >= 5)
          || (tier === totalPets && totalPets >= 2 && totalPets <= 4);
        badge.classList.toggle('pet-bundle__tier-badge--active', active);
      });
    }

    /* ------------------------------------------------------------------ */
    /*  Validation (upload is REQUIRED, auto-clear errors on interaction)  */
    /* ------------------------------------------------------------------ */

    _validateCards() {
      const errors = [];
      const cards = this.cardsContainer.querySelectorAll('.pet-card');
      this._firstInvalidCard = null;

      cards.forEach((card, i) => {
        const data = this._getCardData(card);
        const num = i + 1;
        const errorsBefore = errors.length;

        // Name
        const nameInput = card.querySelector('.pet-card__name-input');
        if (!data.petName) {
          errors.push(`Pet ${num}: Please enter a pet name`);
          nameInput?.classList.add('pet-card__input--error');
        } else {
          nameInput?.classList.remove('pet-card__input--error');
        }

        // Eye color (from pill selection, stored in hidden input)
        const eyePills = card.querySelector('.pet-card__eye-pills');
        if (!data.eyeColor) {
          errors.push(`Pet ${num}: Please select an eye color`);
          eyePills?.classList.add('pet-card__eye-pills--error');
        } else {
          eyePills?.classList.remove('pet-card__eye-pills--error');
        }

        // Upload — REQUIRED (must be a successful HTTP URL, not just a local file)
        const uploadZone = card.querySelector('.pet-card__upload-zone');
        const pondInstance = this._filePondInstances.get(String(data.index));
        const hasUploadedUrl = data.uploadUrl && data.uploadUrl.startsWith('http');
        const hasLocalFile = pondInstance && pondInstance.getFiles().length > 0;

        if (!hasUploadedUrl) {
          if (hasLocalFile) {
            errors.push(`Pet ${num}: Photo is still uploading. Please wait a moment and try again`);
            this._trackUploadIssue('submit_while_uploading', { pet: num });
          } else {
            errors.push(`Pet ${num}: Please upload a photo of your pet`);
          }
          uploadZone?.classList.add('pet-card__upload-zone--error');
        } else {
          uploadZone?.classList.remove('pet-card__upload-zone--error');
        }

        if (errors.length > errorsBefore && !this._firstInvalidCard) {
          this._firstInvalidCard = card;
        }
      });

      return errors;
    }

    /* ------------------------------------------------------------------ */
    /*  Cart Submission                                                     */
    /* ------------------------------------------------------------------ */

    async _addToCart() {
      if (this._isSubmitting) return;
      this._hideError();


      const errors = this._validateCards();
      if (errors.length) {
        this._showError(errors[0]);
        if (this._firstInvalidCard && this._firstInvalidCard.classList.contains('pet-card--collapsed')) {
          this._setActiveCard(this._firstInvalidCard);
        }
        return;
      }

      this._setLoading(true);

      try {
        const items = this._buildCartItems();
        const res = await fetch(this.config.routes.cartAddUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ items }),
        });

        if (!res.ok) {
          let msg = 'Could not add items to cart.';
          try { const d = await res.json(); msg = d.description || d.message || msg; } catch (e) {}
          throw new Error(msg);
        }

        // Notify Dawn
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          try { publish(PUB_SUB_EVENTS.cartUpdate, { source: 'pet-bundle-builder' }); } catch (e) {}
        }
        document.dispatchEvent(new CustomEvent('cart:updated'));

        // Successfully added to cart — clear saved form state
        this._clearState();

        // Open cart drawer instead of redirecting to /cart
        this._setLoading(false);
        document.dispatchEvent(new CustomEvent('cart:open'));
      } catch (err) {
        this._showError(err.message);
        this._setLoading(false);
      }
    }


    _buildCartItems() {
      // Extras never block the purchase; count the ones that miss the cart
      // (still uploading or failed at click time) so we know if this edge
      // is real. Photo 1 is guaranteed by _validateCards.
      let unfinished = 0;
      this._filePondInstances.forEach((pond) => {
        unfinished += pond.getFiles().filter((f) => !(typeof f.serverId === 'string' && f.serverId.startsWith('http'))).length;
      });
      if (unfinished > 0) this._trackUploadIssue('dropped_extras', { count: unfinished });

      const petData = this._getAllCardData();
      const total = petData.reduce((s, p) => s + p.quantity, 0);
      const discountAmount = this._getDiscountAmountCents(total);

      return petData.map((pet, i) => {
        const props = {};
        if (pet.petName) props['Pet Name'] = pet.petName;
        if (pet.eyeColor) props['Eye Color'] = pet.eyeColor;
        if (pet.uploadUrl && pet.uploadUrl.startsWith('http')) {
          props['Pet Photo'] = pet.uploadUrl;
          // Extra angles: the underscore prefix hides them at checkout while
          // the order webhook still collects every photo-URL property.
          (pet.uploadUrls || []).slice(1, 4).forEach((u, j) => {
            if (u && u.startsWith('http') && u !== pet.uploadUrl) {
              props[`_Pet Photo ${j + 2}`] = u;
            }
          });
        }

        return {
          id: parseInt(pet.variantId, 10),
          quantity: pet.quantity,
          properties: props,
        };
      });
    }

    /* ------------------------------------------------------------------ */
    /*  UI Helpers                                                         */
    /* ------------------------------------------------------------------ */

    _showError(msg) {
      if (this.errorEl) {
        this.errorEl.textContent = msg;
        this.errorEl.hidden = false;
        this.errorEl.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    }

    _hideError() {
      if (this.errorEl) { this.errorEl.hidden = true; this.errorEl.textContent = ''; }
    }

    /**
     * Track upload issues via Klaviyo for alerting.
     * @param {'timeout'|'upload_error'|'submit_while_uploading'} type
     * @param {Object} details - { pet, error? }
     */
    _trackUploadIssue(type, details = {}) {
      try {
        const isMobile = window.innerWidth < 750;
        const payload = {
          type,
          pet_number: details.pet || 0,
          photo_count: details.count || 0,
          error_message: details.error || '',
          page_url: window.location.href,
          device: isMobile ? 'mobile' : 'desktop',
          user_agent: navigator.userAgent,
          timestamp: new Date().toISOString()
        };
        // Klaviyo JS API (loaded by Shopify app)
        window._learnq = window._learnq || [];
        window._learnq.push(['track', 'Photo Upload Issue', payload]);
      } catch (e) {
        // Silent — never block the user experience for tracking
      }
    }

    /**
     * Reset the upload stall-timeout. Called on file add + each progress event.
     * If no progress arrives within the stall window, warn the user. Mobile
     * networks are slower — give them more room.
     */
    _resetUploadTimeout(cardIndex, card) {
      const prev = this._uploadTimeouts.get(String(cardIndex));
      if (prev) clearTimeout(prev);

      const isMobile = window.innerWidth < 750;
      const STALL_MS = isMobile ? 60000 : 30000;
      const card2 = this.cardsContainer.querySelector(`.pet-card[data-pet-index="${cardIndex}"]`);
      const num = card2 ? this._petNumberFor(card2) : 1;
      const uploadUrlInput = card.querySelector('.pet-card__upload-url');

      const timeoutId = setTimeout(() => {
        if (!uploadUrlInput || !uploadUrlInput.value || !uploadUrlInput.value.startsWith('http')) {
          this._showError(`Pet ${num}: Upload is taking longer than expected. Please remove and re-upload your photo.`);
          this._trackUploadIssue('timeout', { pet: num });
        }
        this._uploadTimeouts.delete(String(cardIndex));
      }, STALL_MS);

      this._uploadTimeouts.set(String(cardIndex), timeoutId);
    }

    /* ------------------------------------------------------------------ */
    /*  Form Persistence — localStorage auto-save/restore                  */
    /* ------------------------------------------------------------------ */

    _storageKey() {
      return `pet-bundle-state-v1:${this.sectionId || 'default'}`;
    }

    /**
     * Serialize current form state and save to localStorage.
     * Called on every meaningful field change.
     */
    _saveState() {
      try {
        if (!this.cardsContainer) return;
        const cards = Array.from(this.cardsContainer.querySelectorAll('.pet-card')).map((card) => {
          const data = this._getCardData(card);
          const activeSpecies = card.querySelector('.pet-card__species-pill--active');
          return {
            petName: data.petName,
            eyeColor: data.eyeColor,
            species: activeSpecies?.dataset.petType || 'Dog',
            variantId: data.variantId,
            uploadUrl: (data.uploadUrl && data.uploadUrl.startsWith('http')) ? data.uploadUrl : '',
            uploadUrls: (data.uploadUrls || []).filter(u => typeof u === 'string' && u.startsWith('http')),
            thumbUrl: (data.thumbUrl && data.thumbUrl.startsWith('http')) ? data.thumbUrl : '',
            quantity: data.quantity,
            eyeUserChose: card.dataset.eyeUserChose === 'true'
          };
        });
        const state = {
          version: 1,
          timestamp: Date.now(),
          petCount: cards.length,
          cards
        };
        localStorage.setItem(this._storageKey(), JSON.stringify(state));
      } catch (e) {
        // Silently ignore quota errors / private mode — don't break the UX
      }
    }

    /**
     * Load saved state from localStorage and apply to the form.
     * Called once on connectedCallback. Expires after 24 hours.
     */
    _restoreState() {
      try {
        const raw = localStorage.getItem(this._storageKey());
        if (!raw) return;

        const state = JSON.parse(raw);
        if (!state || state.version !== 1 || !Array.isArray(state.cards)) return;

        // 24h TTL
        const age = Date.now() - (state.timestamp || 0);
        if (age > 24 * 60 * 60 * 1000) {
          this._clearState();
          return;
        }

        // Nothing to restore if all fields were empty
        const hasData = state.cards.some(c => c.petName || c.uploadUrl || c.eyeUserChose);
        if (!hasData) return;

        // Ensure we have the right number of cards
        const targetCount = Math.max(1, Math.min(10, state.cards.length || state.petCount || 1));
        this._setPetCount(targetCount);
        this._syncCountSelector();

        // Apply saved fields to each card
        const cards = Array.from(this.cardsContainer.querySelectorAll('.pet-card'));
        cards.forEach((card, i) => {
          const saved = state.cards[i];
          if (!saved) return;

          // Species pill + variant
          if (saved.species) {
            card.querySelectorAll('.pet-card__species-pill').forEach(p => {
              const match = p.dataset.petType === saved.species;
              p.classList.toggle('pet-card__species-pill--active', match);
            });
            const variantInput = card.querySelector('.pet-card__variant-input');
            if (variantInput && saved.variantId) variantInput.value = saved.variantId;
          }

          // Pet name
          const nameInput = card.querySelector('.pet-card__name-input');
          if (nameInput && saved.petName) nameInput.value = saved.petName;

          // Eye color
          if (saved.eyeColor) {
            card.querySelectorAll('.pet-card__eye-pill').forEach(p => {
              p.classList.toggle('pet-card__eye-pill--active', (p.dataset.color || '') === saved.eyeColor);
            });
            const eyeValue = card.querySelector('.pet-card__eye-value');
            if (eyeValue) eyeValue.value = saved.eyeColor;
          }
          if (saved.eyeUserChose) card.dataset.eyeUserChose = 'true';

          // Quantity
          const qtyInput = card.querySelector('.pet-card__qty-input');
          if (qtyInput && saved.quantity) qtyInput.value = saved.quantity;

          // Upload URLs (only server-hosted URLs — data URLs aren't restored)
          if (saved.uploadUrl && saved.uploadUrl.startsWith('http')) {
            const uploadInput = card.querySelector('.pet-card__upload-url');
            if (uploadInput) uploadInput.value = saved.uploadUrl;
          }
          if (Array.isArray(saved.uploadUrls)) {
            const urls = saved.uploadUrls.filter(u => typeof u === 'string' && u.startsWith('http'));
            if (urls.length) card.dataset.uploadUrls = JSON.stringify(urls);
          }
          if (saved.thumbUrl && saved.thumbUrl.startsWith('http')) {
            card.dataset.thumbUrl = saved.thumbUrl;
          }
        });

        this._updateCardBadgeThumbnails();

        // Returning shoppers get the compact stack: complete cards as rows,
        // the first unfinished card (if any) open for work.
        const restoredCards = Array.from(this.cardsContainer.querySelectorAll('.pet-card'));
        const firstIncomplete = restoredCards.find((card) => !this._isCardComplete(card));
        this._setActiveCard(firstIncomplete || null);
      } catch (e) {
        // Corrupt state — discard
        this._clearState();
      }
    }

    _clearState() {
      try { localStorage.removeItem(this._storageKey()); } catch (e) {}
    }

    /**
     * Update the per-card upload progress indicator.
     * @param {HTMLElement} card
     * @param {number|null} progress - 0..1 or null to hide
     */
    _updateUploadProgress(card, progress) {
      if (!card) return;
      const zone = card.querySelector('.pet-card__upload-zone');
      if (!zone) return;

      let bar = zone.parentElement.querySelector('.pet-card__upload-progress');
      if (progress == null || progress >= 1) {
        if (bar) bar.remove();
        return;
      }

      if (!bar) {
        bar = document.createElement('div');
        bar.className = 'pet-card__upload-progress';
        bar.innerHTML = `
          <div class="pet-card__upload-progress-track"><div class="pet-card__upload-progress-fill"></div></div>
          <span class="pet-card__upload-progress-label">Uploading 0%</span>
        `;
        zone.parentElement.appendChild(bar);
      }
      const pct = Math.max(0, Math.min(100, Math.round(progress * 100)));
      const fill = bar.querySelector('.pet-card__upload-progress-fill');
      const label = bar.querySelector('.pet-card__upload-progress-label');
      if (fill) fill.style.width = pct + '%';
      if (label) label.textContent = `Uploading ${pct}%`;
    }

    _setLoading(on) {
      this._isSubmitting = on;
      const b = this.addToCartBtn;
      if (!b) return;
      b.disabled = on;
      b.classList.toggle('pet-bundle__submit--loading', on);
      const spinner = b.querySelector('.pet-bundle__submit-loading');
      if (spinner) spinner.hidden = !on;
    }

    _esc(str) {
      const d = document.createElement('div');
      d.textContent = str;
      // QA-14: also neutralize quotes - this output lands inside attributes.
      return d.innerHTML.replace(/"/g, '&quot;').replace(/'/g, '&#39;');
    }

    /** Screen readers hear selection state; class toggles alone are visual. */
    _reflectPressed(container, selector, activeClass) {
      if (!container) return;
      container.querySelectorAll(selector).forEach((el) => {
        el.setAttribute('aria-pressed', el.classList.contains(activeClass) ? 'true' : 'false');
      });
    }

    /** Displayed pet number = position among current cards (1-based). */
    _petNumberFor(card) {
      const cards = Array.from(this.cardsContainer.querySelectorAll('.pet-card'));
      const i = cards.indexOf(card);
      return i >= 0 ? i + 1 : 1;
    }
  }

  customElements.define('pet-bundle-builder', PetBundleBuilder);
}
